import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocationNormalized } from "vue-router";
import { SidebarMenuItem } from "@/types/navigation";
import { useUserStore } from "./stores/user.store";
import { useAppStore } from "./stores/app.store";
import { useMessagesStore } from "./stores/messages.store";
import { IsUserLoggedIn } from "./lib/auth";
import { i18n } from "./app";

const routes = [
  {
    path: "/",
    component: () => import("@/layouts/DefaultLayout.vue"),
    children: [
      {
        path: "",
        name: "Home",
        component: () => import("@/views/HomeView.vue")
      },
      {
        path: "/login",
        name: "SignIn",
        component: () => import("@/views/SignInView.vue"),
        meta: {
          requiresAuth: false
        }
      },
      {
        path: "/forgot",
        name: "PasswordReset",
        component: () => import("@/views/PasswordResetView.vue"),
        meta: {
          requiresNotSignedIn: true,
          requiresAuth: false
        }
      },
      {
        path: "/password/token/:userId/:token?",
        name: "EnterPasswordResetToken",
        component: () => import("@/views/InputPasswordResetToken.vue"),
        meta: {
          requiresNotSignedIn: true,
          requiresAuth: false
        }
      },
      {
        path: "/register/verify",
        name: "Register",
        component: () => import("@/views/RegisterVerification.vue"),
        meta: {
          requiresNotSignedIn: true,
          requiresAuth: false
        }
      }
    ]
  },
  {
    path: "/admintools",
    component: () => import("@/layouts/AdminToolsLayout.vue"),
    redirect: "/admintools/lookup-user-data",
    meta: {
      requiresSystemAdmin: true
    },
    children: [
      {
        path: "lookup-user-data",
        name: "lookupUserData",
        component: () => import("@/views/admintools/LookupUserDataView.vue"),
        meta: {
          menuItem: SidebarMenuItem.LookupUserData
        }
      },
      {
        path: "roster-sync-dashboard",
        name: "RosterSyncDashboard",
        component: () => import("@/views/admintools/SyncDashboardView.vue"),
        meta: {
          menuItem: SidebarMenuItem.RosterSyncDashboard
        }
      },
      {
        path: "audit-logs",
        name: "AuditLogs",
        component: () => import("@/views/admintools/AuditLogView.vue"),
        meta: {
          menuItem: SidebarMenuItem.AuditLogs
        }
      }
    ]
  },
  {
    path: "/:organization",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      {
        path: "",
        name: "General Information",
        component: () => import("@/views/GeneralInformationView.vue"),
        meta: {
          menuItem: SidebarMenuItem.GeneralInformation
        }
      },
      {
        path: "licenses",
        name: "Licenses",
        component: () => import("@/views/LicenseView.vue"),
        meta: {
          menuItem: SidebarMenuItem.Licenses
        }
      },
      {
        path: "users",
        name: "Users",
        component: () => import("@/views/UsersView.vue"),
        meta: {
          menuItem: SidebarMenuItem.Users
        }
      },
      {
        path: "sub-organizations",
        name: "Sub organizations",
        component: () => import("@/views/SubOrganizationsView.vue"),
        meta: {
          menuItem: SidebarMenuItem.SubOrganizations
        }
      },
      {
        path: "credit-log",
        name: "Credit log",
        component: () => import("@/views/CreditLogView.vue"),
        meta: {
          menuItem: SidebarMenuItem.CreditLog
        }
      }
    ]
  }
];

// Development-only routes
const devRoutes = [
  {
    path: "/demo",
    name: "ComponentsShowcase",
    component: () => import("@/views/ComponentsShowcaseView.vue"),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: "/:organization",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      {
        path: "admin",
        name: "AdminPanel",
        component: () => import("@/views/AdminPanelView.vue"),
        meta: {
          menuItem: "Admin Panel",
          requiresAuth: true
        }
      }
    ]
  }
];

const isDevelopment = import.meta.env.DEV;
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes, ...(isDevelopment ? devRoutes : [])]
});

// Navigation guard to handle authentication
router.beforeEach(async (to: RouteLocationNormalized) => {
  const appStore = useAppStore();
  if(to.meta.menuItem) {
    appStore.selectedNavMenuItem = to.meta.menuItem as SidebarMenuItem;
  }

  if(to.meta.requiresAuth === true || to.meta.requiresAuth === undefined) {
    try {
      const userStore = useUserStore();
      const isAuthenticated = await IsUserLoggedIn(userStore.userId ?? "", userStore.userEmail ?? "");

      if(!isAuthenticated) {
        userStore.clearUserSession();
        return { name: "SignIn" };
      }
    }
    catch(error) {
      console.error("Auth check failed:", error);
      return { name: "SignIn" };
    }
  }
  return true;
});

// Navigation guard to restrict system admin routes
router.beforeEach((to: RouteLocationNormalized) => {
  const requiresSystemAdmin = to.matched.some(record => record.meta.requiresSystemAdmin);

  if(requiresSystemAdmin) {
    const userStore = useUserStore();

    if(!userStore.isSystemAdmin) {
      return { name: "Home" };
    }
  }

  return true;
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  // Handle organization selection from URL
  const appStore = useAppStore();

  if(to.params.organization && typeof to.params.organization === "string") {
    const orgId = to.params.organization;
    // Check if organization needs to be updated (not set or different from URL)
    if(!appStore.selectedOrganization || appStore.selectedOrganization.id !== orgId) {
      try {
        await appStore.selectAndFetchOrganization(orgId);
      }
      catch(error) {
        console.error("Failed to fetch organization from URL:", error);
      }
    }
  }
  return true;
});

// Handle OAuth callback for integrations
router.beforeEach((to: RouteLocationNormalized) => {
  const appStore = useAppStore();
  const integration = to.query.integration as string | undefined;
  const status = to.query.status as string | undefined;
  const message = to.query.message as string | undefined;

  if(integration && status) {
    // Store callback state
    appStore.setIntegrationOAuthCallback({
      integration,
      status,
      message: message || undefined
    });

    // Clean up URL parameters
    return { path: to.path, query: {} };
  }

  return true;
});

// Show error snackbar after navigation completes (when DashboardLayout is mounted)
router.afterEach((_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const appStore = useAppStore();
  const callback = appStore.integrationOAuthCallback;

  if(callback && callback.status === "error" && !callback.errorShown) {
    // Use setTimeout to ensure DashboardLayout is mounted and snackbar queue is ready
    setTimeout(() => {
      const messagesStore = useMessagesStore();
      const errorMessage = callback.message || i18n.global.t("integration_failed_to_connect", { integration: callback.integration });
      messagesStore.error(errorMessage);
      // Mark error as shown to prevent showing it again on subsequent navigations
      appStore.setIntegrationOAuthCallback({
        ...callback,
        errorShown: true
      });
    }, 100);
  }
});

export default router;
